import {
  ActionType,
  AutoTextField,
  BooleanField,
  CheckboxGroupField,
  DatePickerField,
  SelectAutocompleteField,
  SelectField,
} from '../../components/AutoForm/AutoFieldNew';
import {
  ContributionFrequencyTypes,
  ContributionPaymentDateTypes,
  ContributionSourceTypes,
  ContributionTypes,
  FormTransferTypes,
  FormTypes,
  ISAFormSubscriptionTypes,
  JISAFormSubscriptionTypes,
  NomineeTransferFormTypes,
  ReportPackFormStatus,
  TransferSchemeTypes,
  TransferDrawdownTypes,
  IsaTransferTypes,
} from '../../schema-types/globalTypes';
import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';
import {titleField} from '../AddExtAccountPage/addAccountFields';
import {UploadDocumentFormType} from './ReportPackHelpers';

export type ReportPackGenericFormType = {
  formData: ReportPackFormFragment;
  onClose: () => void;
  disabled: boolean;
};

export type ReportPackGenericFormInnerType = {
  disabled: boolean;
};

export type reportPackFormType = {
  id: string;
  version: string | null;
  reportPackId: string;
  contactCuroId: string;
  formType: FormTypes;
  formTypeLabel: string | null;
  formTransferType: FormTransferTypes | null;
  formTransferTypeLabel: string | null;
  status: ReportPackFormStatus;
  title: string | null;
  forenames: string | null;
  surname: string | null;
  address: string | null;
  postcode: string | null;
  dob: string | null;
  niNumber: string | null;
  isaManagerName: string | null;
  isaManagerAddress1: string | null;
  isaManagerAddress2: string | null;
  isaManagerPostcode: string | null;
  isaManagerSortCode: string | null;
  isaManagerCity: string | null;
  isaManagerPlanRef: string | null;
  isaAmount: string;
  isaFormSubscriptionType: ISAFormSubscriptionTypes | null;
  isaTransferAmount: any | null;
  isaTransferType: IsaTransferTypes | null;
  childTitle: string | null;
  childForenames: string | null;
  childSurname: string | null;
  childDob: string | null;
  childNiNumber: string | null;
  childAddress: string | null;
  childPostcode: string | null;
  taxYear: string | null;
  jisaFormSubscriptionType: JISAFormSubscriptionTypes | null;
  nomineeFormTransferType: NomineeTransferFormTypes | null;
  providerAccountNumber: string | null;
  managerAddress1: string | null;
  managerAddress2: string | null;
  managerCity: string | null;
  managerCompanyName: string | null;
  managerPhoneNumber: string | null;
  managerPostcode: string | null;
  managerTradingName: string | null;
  adviceReceived: boolean;
  adviserFirm: string | null;
  adviserName: string | null;
  contributionAmount: string | null;
  contributionFrequencyType: ContributionFrequencyTypes | null;
  contributionPaymentDateType: ContributionPaymentDateTypes | null;
  contributionSourceType: ContributionSourceTypes | null;
  contributionType: ContributionTypes | null;
  sippNumber: string | null;
  sippTransferSchemeType: TransferSchemeTypes | null;
  sippTransferDrawdownType: TransferDrawdownTypes | null;
  sippTransferContinueIncome: boolean | null;
  sippTransferDisqualifyingCredits: boolean | null;
  sippTransferFlexiAccessConvert: boolean | null;
  sippTransferFullValue: boolean | null;
  sippTransferApproximateValue: any | null;
  sippTransferPlanRef: string | null;
  sippTransferAssetsInSpecie: string | null;
  bwPlanNumber?: string | null;
  furtherActionRequiredMessage: string | null;
};

export const reportPackISAManagerField: AutoTextField<reportPackFormType> = {
  id: 'isaManagerName',
  type: ActionType.text,
  label: 'ISA Manager',
};

export const reportPackSortCodeField: AutoTextField<reportPackFormType> = {
  id: 'isaManagerSortCode',
  type: ActionType.text,
  label: 'Sort code',
};

export const NIField: AutoTextField<reportPackFormType> = {
  id: 'niNumber',
  type: ActionType.text,
  label: 'NI Number',
};

export const addressField: AutoTextField<reportPackFormType> = {
  id: 'address',
  type: ActionType.text,
  label: 'Address line 1',
};

export const postcodeField: AutoTextField<reportPackFormType> = {
  id: 'postcode',
  type: ActionType.text,
  label: 'Postcode',
};

export const DOBField: DatePickerField<reportPackFormType> = {
  id: 'dob',
  type: ActionType.datepicker,
  label: 'Date of birth',
};

export const childDOBField: DatePickerField<reportPackFormType> = {
  id: 'childDob',
  type: ActionType.datepicker,
  label: 'Child date of birth',
};

export const childForenamesField: AutoTextField<reportPackFormType> = {
  id: 'childForenames',
  type: ActionType.text,
  label: 'Child forename(s)',
};

export const childSurnameField: AutoTextField<reportPackFormType> = {
  id: 'childSurname',
  type: ActionType.text,
  label: 'Child surname',
};

export const childNIField: AutoTextField<reportPackFormType> = {
  id: 'childNiNumber',
  type: ActionType.text,
  label: 'Child NI Number',
};

export const childTitleField: SelectAutocompleteField<reportPackFormType> = {
  ...titleField,
  id: 'childTitle',
  label: 'Child Title',
};

export const childAddressField: AutoTextField<reportPackFormType> = {
  id: 'childAddress',
  type: ActionType.text,
  label: 'Child Address',
};

export const childPostcodeField: AutoTextField<reportPackFormType> = {
  id: 'childPostcode',
  type: ActionType.text,
  label: 'Child Postcode',
};

export const isaManagerPostcodeField: AutoTextField<reportPackFormType> = {
  id: 'isaManagerPostcode',
  type: ActionType.text,
  label: 'ISA Manager postcode',
};
export const isaManagerCityField: AutoTextField<reportPackFormType> = {
  id: 'isaManagerCity',
  type: ActionType.text,
  label: 'ISA Manager city',
};

export const isaManagerAddress1Field: AutoTextField<reportPackFormType> = {
  id: 'isaManagerAddress1',
  type: ActionType.text,
  label: 'ISA Manager address line 1',
};
export const isaManagerAddress2Field: AutoTextField<reportPackFormType> = {
  id: 'isaManagerAddress2',
  type: ActionType.text,
  label: 'ISA Manager address line 2',
};

export const isaTransferTypeField: SelectField<reportPackFormType, FormTransferTypes> = {
  id: 'formTransferType',
  type: ActionType.select,
  label: 'Form Transfer type',
  options: [
    {
      label: 'Stocks & Shares ISA Transfer in Cash',
      value: FormTransferTypes.HOLDINGS,
    },
    {
      label: 'Stocks & Shares ISA Transfer in-specie',
      value: FormTransferTypes.STOCKS,
    },
    {
      label: 'Cash ISA Transfer in cash',
      value: FormTransferTypes.CASH,
    },
  ],
};

export const reportPackAccountField: AutoTextField<reportPackFormType> = {
  id: 'isaManagerPlanRef',
  type: ActionType.text,
  label: 'Account number',
};

export const reportPackTaxYearField: AutoTextField<reportPackFormType> = {
  id: 'taxYear',
  type: ActionType.text,
  label: 'Tax year - Please use this format YYYY/YY',
};

export const reportPackIsaFormSubscriptionTypeField: SelectField<
  reportPackFormType,
  ISAFormSubscriptionTypes
> = {
  id: 'isaFormSubscriptionType',
  type: ActionType.select,
  label: 'Subscription type',
  options: [
    {
      label: 'Bank Transfer',
      value: ISAFormSubscriptionTypes.BANK_TRANSFER,
    },
    {
      label: 'Cheque',
      value: ISAFormSubscriptionTypes.CHEQUE,
    },
    {
      label: 'Direct Debit',
      value: ISAFormSubscriptionTypes.DIRECT_DEBIT,
    },
    {
      label: 'Existing Account',
      value: ISAFormSubscriptionTypes.EXISTING_ACCOUNT,
    },
  ],
};

export const reportPackJisaFormSubscriptionTypeField: SelectField<
  reportPackFormType,
  JISAFormSubscriptionTypes
> = {
  id: 'jisaFormSubscriptionType',
  type: ActionType.select,
  label: 'Subscription type',
  options: [
    {
      label: 'BACS',
      value: JISAFormSubscriptionTypes.BACS,
    },
    {
      label: 'Direct Debit',
      value: JISAFormSubscriptionTypes.DIRECT_DEBIT,
    },
    {
      label: 'Cheque',
      value: JISAFormSubscriptionTypes.CHEQUE,
    },
  ],
};

export const reportPackIsaAmountField: AutoTextField<reportPackFormType> = {
  id: 'isaAmount',
  type: ActionType.text,
  label: 'Amount',
  currency: true,
};

export const reportPackNomineeTransferFormTypeField: SelectField<
  reportPackFormType,
  NomineeTransferFormTypes
> = {
  id: 'nomineeFormTransferType',
  type: ActionType.select,
  label: 'Transfer type',
  options: [
    {
      label: 'Transfer in Cash',
      value: NomineeTransferFormTypes.HOLDINGS,
    },
    {
      label: 'Transfer in-specie',
      value: NomineeTransferFormTypes.STOCKS,
    },
  ],
};

export const reportPackProviderAccountNumberField: AutoTextField<reportPackFormType> = {
  id: 'providerAccountNumber',
  type: ActionType.text,
  label: 'Provider Account Number',
};

export const reportPackManagerCompanyNameField: AutoTextField<reportPackFormType> = {
  id: 'managerCompanyName',
  type: ActionType.text,
  label: 'Manager Company Name',
};
export const reportPackManagerTradingNameField: AutoTextField<reportPackFormType> = {
  id: 'managerTradingName',
  type: ActionType.text,
  label: 'Manager Trading Name',
};

export const reportPackManagerPhoneNumberField: AutoTextField<reportPackFormType> = {
  id: 'managerPhoneNumber',
  type: ActionType.text,
  label: 'Manager phone number',
};
export const reportPackManagerPostcodeField: AutoTextField<reportPackFormType> = {
  id: 'managerPostcode',
  type: ActionType.text,
  label: 'Manager postcode',
};
export const reportPackManagerCityField: AutoTextField<reportPackFormType> = {
  id: 'managerCity',
  type: ActionType.text,
  label: 'Manager city',
};

export const reportPackManagerAddress1Field: AutoTextField<reportPackFormType> = {
  id: 'managerAddress1',
  type: ActionType.text,
  label: 'Manager address line 1',
};
export const reportPackManagerAddress2Field: AutoTextField<reportPackFormType> = {
  id: 'managerAddress2',
  type: ActionType.text,
  label: 'Manager address line 2',
};

export const reportPackContributionAmountField: AutoTextField<reportPackFormType> = {
  id: 'contributionAmount',
  type: ActionType.text,
  label: 'Amount',
  currency: true,
};

export const reportPackSIPPNumberField: AutoTextField<reportPackFormType> = {
  id: 'sippNumber',
  type: ActionType.text,
  label: 'SIPP Number',
};

export const reportPackContributionFrequencyTypeField: SelectField<
  reportPackFormType,
  ContributionFrequencyTypes
> = {
  id: 'contributionFrequencyType',
  type: ActionType.select,
  label: 'Frequency type',
  options: [
    {
      label: 'Annually',
      value: ContributionFrequencyTypes.ANNUALLY,
    },
    {
      label: 'Half-Yearly',
      value: ContributionFrequencyTypes.HALF_YEARLY,
    },
    {
      label: 'Monthly',
      value: ContributionFrequencyTypes.MONTHLY,
    },
    {
      label: 'Quarterly',
      value: ContributionFrequencyTypes.QUARTERLY,
    },
  ],
};

export const reportPackContributionPaymentDateTypeField: SelectField<
  reportPackFormType,
  ContributionPaymentDateTypes
> = {
  id: 'contributionPaymentDateType',
  type: ActionType.select,
  label: 'Payment Date type',
  options: [
    {
      label: '15th',
      value: ContributionPaymentDateTypes.FIFTEENTH,
    },
    {
      label: '1st',
      value: ContributionPaymentDateTypes.FIRST,
    },
  ],
};

export const reportPackContributionSourceTypeField: SelectField<
  reportPackFormType,
  ContributionSourceTypes
> = {
  id: 'contributionSourceType',
  type: ActionType.select,
  label: 'Source type',
  options: [
    {
      label: 'Employer',
      value: ContributionSourceTypes.EMPLOYER,
    },
    {
      label: 'Personal',
      value: ContributionSourceTypes.PERSONAL,
    },
  ],
};

export const reportPackContributionTypeField: SelectField<
  reportPackFormType,
  ContributionTypes
> = {
  id: 'contributionType',
  type: ActionType.select,
  label: 'Contribution type',
  options: [
    {
      label: 'Regular',
      value: ContributionTypes.REGULAR,
    },
    {
      label: 'Single',
      value: ContributionTypes.SINGLE,
    },
  ],
};

export const reportPackAdviceReceivedField: BooleanField = {
  id: 'adviceReceived',
  label: 'Advice Received?',
  type: ActionType.boolean,
  initialValue: false,
  noValue: false,
  yesValue: true,
};

export const reportPackBwPlanNumberField: AutoTextField<reportPackFormType> = {
  id: 'bwPlanNumber',
  type: ActionType.text,
  label: 'BW Plan Number',
};

export const reportPackSippTransferApproximateValueField: AutoTextField<reportPackFormType> =
  {
    id: 'sippTransferApproximateValue',
    type: ActionType.text,
    label: 'Approximate Value',
    currency: true,
  };

export const reportPackSippTransferContinueIncomeField: BooleanField = {
  id: 'sippTransferContinueIncome',
  label: 'Continue Income?',
  type: ActionType.boolean,
  initialValue: false,
  noValue: false,
  yesValue: true,
};

export const reportPackSippTransferDisqualifyingCreditsField: BooleanField = {
  id: 'sippTransferDisqualifyingCredits',
  label: 'Disqualifying Credits?',
  type: ActionType.boolean,
  initialValue: false,
  noValue: false,
  yesValue: true,
};

export const reportPackSippTransferFlexiAccessConvertField: BooleanField = {
  id: 'sippTransferFlexiAccessConvert',
  label: 'Flexi Access Convert?',
  type: ActionType.boolean,
  initialValue: false,
  noValue: false,
  yesValue: true,
};

export const reportPackSippTransferFullValueField: BooleanField = {
  id: 'sippTransferFullValue',
  label: 'Transfer Full Value?',
  type: ActionType.boolean,
  initialValue: false,
  noValue: false,
  yesValue: true,
};

export const reportPackSippTransferPlanRefField: AutoTextField<reportPackFormType> = {
  id: 'sippTransferPlanRef',
  type: ActionType.text,
  label: 'Plan Ref',
};

export const reportPackSippTransferSchemeTypeField: SelectField<
  reportPackFormType,
  TransferSchemeTypes
> = {
  id: 'sippTransferSchemeType',
  type: ActionType.select,
  label: 'Transfer Scheme type',
  options: [
    {
      label: 'Defined Benefit',
      value: TransferSchemeTypes.DEFINED_BENEFIT,
    },
    {
      label: 'Money Purchase',
      value: TransferSchemeTypes.MONEY_PURCHASE,
    },
    {
      label: 'Personal Pension',
      value: TransferSchemeTypes.PERSONAL_PENSION,
    },
    {
      label: 'Recognised Overseas',
      value: TransferSchemeTypes.RECOGNISED_OVERSEAS,
    },
    {
      label: 'Retirement Anuity',
      value: TransferSchemeTypes.RETIREMENT_ANNUITY,
    },
    {
      label: 'SIPP',
      value: TransferSchemeTypes.SIPP,
    },
    {
      label: 'SSAS',
      value: TransferSchemeTypes.SSAS,
    },
  ],
};

export const reportPackSippTransferDrawdownTypeField: SelectField<
  reportPackFormType,
  TransferDrawdownTypes
> = {
  id: 'sippTransferDrawdownType',
  type: ActionType.select,
  label: 'Transfer Drawdown type',
  options: [
    {
      label: 'Entirely',
      value: TransferDrawdownTypes.ENTIRELY,
    },
    {
      label: 'Partially',
      value: TransferDrawdownTypes.PARTIALLY,
    },
    {
      label: 'None',
      value: TransferDrawdownTypes.NONE,
    },
  ],
};

export const reportPackSippTransferAssetsInSpecieField: AutoTextField<reportPackFormType> =
  {
    id: 'sippTransferAssetsInSpecie',
    type: ActionType.text,
    label: 'Assets In Specie',
  };

export const reportPackIsaTransferAmountField: AutoTextField<reportPackFormType> = {
  id: 'isaTransferAmount',
  type: ActionType.text,
  label: 'Transfer Amount',
  currency: true,
};

export const reportPackIsaTransferTypeField: SelectField<
  reportPackFormType,
  IsaTransferTypes
> = {
  id: 'isaTransferType',
  type: ActionType.select,
  label: 'ISA Transfer type',
  options: [
    {
      label: 'Full',
      value: IsaTransferTypes.FULL,
    },
    {
      label: 'Partial',
      value: IsaTransferTypes.PARTIAL,
    },
  ],
};

export const reportPackGenericDocumentsField: CheckboxGroupField<
  UploadDocumentFormType,
  string
> = {
  id: 'selectedGenericForms',
  type: ActionType.checkboxGroup,
  label: 'Generic Disclosures',
  options: [],
  horizontal: true,
  horizontalOptionWidthXs: 12,
  horizontalOptionWidthMd: 6,
  horizontalOptionWidthLg: 6,
  size: 'small',
  boldLabel: true,
  fullWidth: true,
};

export const reportPackFurtherActionRequiredMessageField: AutoTextField<reportPackFormType> =
  {
    id: 'furtherActionRequiredMessage',
    type: ActionType.text,
    label: 'Further Action Required Message',
  };

export interface AddGenericDocumentFormType {
  files?: File[];
  documentTitle: string;
  version: string;
}

export const reportPackDocumentTitleField: AutoTextField<AddGenericDocumentFormType> = {
  id: 'documentTitle',
  type: ActionType.text,
  label: 'Document Title',
};

export const documentVersionField: AutoTextField = {
  id: 'version',
  type: ActionType.text,
  label: 'Document Version',
  charLimit: 20,
};
